import React, { useState, useEffect, useMemo, useRef } from 'react';
import { auth } from '../firebase';
import BackButton from '../components/BackButton';

// SmartShopAssets - the native Asset Library (Phase A).
// Ports Glen's merge.html Asset Library: two panels (products + image library), usage
// map, Used/Unused filters, smart-sort (selected product's images float up), assign
// images to a product, archive a product. Reads the native `catalogue` (via list-products)
// - NOT the old stage registry/registry_assets - so it matches the Product CMS exactly.
//
// Phase A source = images already on products (derived from catalogue). Phase B (parked):
// add SmartData / migration-tool / third-party sources with a SOURCE filter, backed by a
// native datalake image-enumeration endpoint (shared with the Review portal).
//
// LANDMINE: list-products with no gido_id returns Glen's live catalogue. Always pass the
// logged-in gido. Hard-stop if missing.
//
// Auth: Bearer <firebase id token>, plus gido_id in the body on writes (D-084).
// save-product takes the writable organisation as a verified parameter and proves
// the caller's seat with resolve_gidu - it no longer derives tenancy from the token.

const LIST_URL = 'https://europe-west1-dbos-core-prod.cloudfunctions.net/smartshop-list-products';
const SAVE_URL = 'https://europe-west1-dbos-core-prod.cloudfunctions.net/smartshop-save-product';

const C = {
  bg: '#0a0c0a', bg2: '#0d0f0d', bg3: '#111311', bg4: '#1a1c1a',
  text: '#F4F1E1', accent: 'var(--ss-accent, #8B1A1A)', success: '#4a9e4a',
  faint: 'rgba(255,255,255,0.3)', faint2: 'rgba(255,255,255,0.4)',
  line: 'rgba(255,255,255,0.08)', line2: 'rgba(255,255,255,0.06)',
};

const S = {
  page: { background: C.bg, color: C.text, minHeight: '100vh', fontFamily: 'system-ui, sans-serif' },
  header: { background: C.bg, borderBottom: `1px solid ${C.line}`, padding: '0 28px', height: 56, display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 100 },
  h1: { color: C.accent, fontSize: 16, fontStyle: 'italic', fontWeight: 900, fontFamily: 'Georgia, serif' },
  bar: { padding: '12px 28px', display: 'flex', gap: 12, alignItems: 'center', borderBottom: `1px solid ${C.line2}`, background: C.bg2, flexWrap: 'wrap' },
  // height (not minHeight) + minHeight:0 on the children: a grid child defaults to
  // min-height:auto, so it grows to fit its content and the PAGE scrolls instead of
  // the pane - which is why scrolling the product list dragged the image list with it.
  cols: { display: 'grid', gridTemplateColumns: '1fr 1.3fr', gap: 0, height: 'calc(100vh - 120px)' },
  colProducts: { borderRight: `1px solid ${C.line2}`, padding: 16, overflowY: 'auto', minHeight: 0 },
  colImages: { padding: 16, overflowY: 'auto', minHeight: 0 },
  colTitle: { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: C.faint2, marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  search: { background: C.bg4, border: '1px solid rgba(255,255,255,0.1)', color: C.text, padding: '8px 14px', fontSize: 12, outline: 'none', width: 220 },
  select: { background: C.bg4, border: '1px solid rgba(255,255,255,0.1)', color: C.text, padding: '8px 14px', fontSize: 12, outline: 'none', cursor: 'pointer' },
  pCard: (sel) => ({ background: C.bg3, border: `1px solid ${sel ? C.accent : C.line2}`, marginBottom: 8, padding: 10, cursor: 'pointer' }),
  pHead: { display: 'flex', gap: 10, alignItems: 'center' },
  pThumb: { width: 44, height: 44, objectFit: 'cover', background: C.bg4, flexShrink: 0 },
  pName: { fontSize: 12, fontWeight: 700, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  pMeta: { fontSize: 9, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.06em' },
  pGid: { fontSize: 8, fontFamily: 'monospace', color: 'rgba(255,255,255,0.2)' },
  pCount: { fontSize: 10, color: C.faint, flexShrink: 0 },
  strip: { display: 'flex', gap: 4, marginTop: 8 },
  stripImg: { width: 34, height: 34, objectFit: 'cover', background: C.bg4 },
  filterBtn: (active) => ({ background: active ? C.accent : C.bg4, color: active ? 'white' : C.faint2, border: '1px solid rgba(255,255,255,0.1)', padding: '5px 12px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', cursor: 'pointer' }),
  imgGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 10 },
  imgCard: (assigned, sel) => ({ position: 'relative', background: C.bg3, border: `2px solid ${sel ? C.accent : assigned ? 'rgba(74,158,74,0.4)' : C.line2}`, cursor: 'pointer', overflow: 'hidden' }),
  imgTag: { width: '100%', height: 110, objectFit: 'cover', background: C.bg4, display: 'block' },
  imgInfo: { padding: '6px 8px' },
  imgFname: { fontSize: 10, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  imgUsed: { fontSize: 9, color: C.success, marginTop: 2 },
  imgUnused: { fontSize: 9, color: 'rgba(255,255,255,0.25)', marginTop: 2 },
  assignedBadge: { position: 'absolute', top: 4, left: 4, background: 'rgba(74,158,74,0.9)', color: 'white', fontSize: 8, fontWeight: 700, padding: '2px 5px', textTransform: 'uppercase', zIndex: 2 },
  btn: (kind) => ({
    border: 'none', padding: '9px 16px', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.1em', cursor: 'pointer',
    ...(kind === 'save' ? { background: C.accent, color: 'white' } : {}),
    ...(kind === 'ghost' ? { background: 'rgba(255,255,255,0.05)', color: C.faint2, border: '1px solid rgba(255,255,255,0.1)' } : {}),
    ...(kind === 'danger' ? { background: 'transparent', color: 'rgba(139,26,26,0.8)', border: '1px solid rgba(139,26,26,0.35)' } : {}),
  }),
  statusBar: (kind) => ({ position: 'fixed', bottom: 0, left: 0, right: 0, padding: '10px 28px', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', color: kind === 'error' ? C.accent : kind === 'success' ? C.success : C.faint, background: '#080a08', borderTop: `1px solid ${C.line2}`, textAlign: 'center', zIndex: 200 }),
};

async function authFetch(url, opts = {}) {
  const token = await auth.currentUser.getIdToken();
  return fetch(url, { ...opts, headers: { ...(opts.headers || {}), Authorization: `Bearer ${token}` } });
}

// Rebuild a product's full save-product body (save-product requires name+category every write).
function productToBody(p, imagesOverride, statusOverride, gidoId) {
  const imgs = imagesOverride || { primary: p.primary_image || '', secondary: p.secondary_image || '', all: p.all_assets || [] };
  return {
    gido_id: gidoId,               // D-084 - verified server-side by resolve_gidu
    product_id: p.product_id,
    slug: p.slug,
    name: p.name,
    category: p.category,
    description: p.description,
    technical_specs: p.technical_specs,
    price_euros: p.price != null ? p.price : 0,
    price_type: p.price_type || 'fixed',
    stock_status: p.stock_status || 'in_stock',
    featured: Boolean(p.featured),
    published: statusOverride === 'archived' ? false : Boolean(p.published),
    status: statusOverride || p.status || 'active',
    large_box: Boolean(p.large_box),
    hygiene_sensitive: Boolean(p.hygiene_sensitive),
    images: imgs,
    variants: p.variants || [],
    options: p.options || [],
  };
}

function SmartShopAssets({ gido, onBack }) {
  const activeGido = gido?.gido_id || null;
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatusState] = useState({ msg: 'Ready', kind: '' });
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [selectedImages, setSelectedImages] = useState(new Set());
  const imagesPaneRef = useRef(null);

  // Escape clears the multi-select, as Glen's original merge.html did - deselecting
  // twelve images one by one is the kind of thing that makes a tool feel hostile.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setSelectedImages(new Set()); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const [imgFilter, setImgFilter] = useState('all'); // all | used | unused
  const [pSearch, setPSearch] = useState('');
  const [pCat, setPCat] = useState('');

  const setStatus = (msg, kind = '') => setStatusState({ msg, kind });

  async function load() {
    if (!activeGido) { setLoading(false); setStatus('No shop identity - cannot load.', 'error'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${LIST_URL}?gido_id=${encodeURIComponent(activeGido)}`);
      const data = await res.json();
      setProducts(data.products || []);
      setStatus(`${(data.products || []).length} products loaded`);
    } catch (e) {
      setStatus('Could not load products', 'error');
    }
    setLoading(false);
  }
  useEffect(() => { load(); }, [activeGido]);

  // ── derive the image library from all products ──────────────────────────
  const { library, usageMap } = useMemo(() => {
    const usage = {}; // url -> [product names]
    const lib = new Map(); // url -> {url, filename, id}
    for (const p of products) {
      const urls = [p.primary_image, p.secondary_image, ...(p.all_assets || [])].filter(Boolean);
      const uniq = [...new Set(urls)].filter((u) => !u.includes('placeholder'));
      for (const url of uniq) {
        if (!usage[url]) usage[url] = [];
        usage[url].push(p.name || p.product_id);
        if (!lib.has(url)) {
          const fname = url.split('/').pop() || url;
          lib.set(url, { url, filename: fname });
        }
      }
    }
    return { library: [...lib.values()], usageMap: usage };
  }, [products]);

  const categories = useMemo(() => [...new Set(products.map((p) => p.category).filter(Boolean))].sort(), [products]);
  const selectedProduct = products.find((p) => p.product_id === selectedProductId) || null;
  const assignedToSelected = useMemo(() => {
    if (!selectedProduct) return new Set();
    return new Set([selectedProduct.primary_image, selectedProduct.secondary_image, ...(selectedProduct.all_assets || [])].filter(Boolean));
  }, [selectedProduct]);

  // filtered + sorted image library
  const shownImages = useMemo(() => {
    let imgs = library.filter((img) => {
      const used = (usageMap[img.url] || []).length > 0;
      if (imgFilter === 'used') return used;
      if (imgFilter === 'unused') return !used;
      return true;
    });
    if (selectedProduct && assignedToSelected.size > 0) {
      imgs = [...imgs].sort((a, b) => (assignedToSelected.has(a.url) ? 0 : 1) - (assignedToSelected.has(b.url) ? 0 : 1));
    }
    return imgs;
  }, [library, usageMap, imgFilter, selectedProduct, assignedToSelected]);

  const shownProducts = useMemo(() => products.filter((p) =>
    (!pSearch || (p.name || '').toLowerCase().includes(pSearch.toLowerCase()) || (p.category || '').toLowerCase().includes(pSearch.toLowerCase())) &&
    (!pCat || p.category === pCat)
  ), [products, pSearch, pCat]);

  function toggleImage(url) {
    setSelectedImages((s) => { const n = new Set(s); n.has(url) ? n.delete(url) : n.add(url); return n; });
  }

  // ── assign selected images to selected product ──────────────────────────
  async function assign() {
    if (!selectedProduct || selectedImages.size === 0) return;
    setStatus(`Assigning ${selectedImages.size} image(s) to ${selectedProduct.name}...`);
    const current = new Set([selectedProduct.primary_image, selectedProduct.secondary_image, ...(selectedProduct.all_assets || [])].filter(Boolean));
    selectedImages.forEach((u) => current.add(u));
    const all = [...current];
    const images = { primary: selectedProduct.primary_image || all[0] || '', secondary: selectedProduct.secondary_image || '', all };
    try {
      const res = await authFetch(SAVE_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(productToBody(selectedProduct, images, null, activeGido)),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) { setStatus(`Assign failed: ${data.reason || res.status}`, 'error'); return; }
      setStatus(`${selectedImages.size} image(s) assigned to ${selectedProduct.name}`, 'success');
      setSelectedImages(new Set());
      await load();
    } catch (e) {
      setStatus(`Network error: ${e.message}`, 'error');
    }
  }

  // ── remove selected images from ALL products (soft: unlink; product data kept) ──
  async function unlinkFromAll() {
    if (selectedImages.size === 0) return;
    if (!window.confirm(`Remove ${selectedImages.size} selected image(s) from every product that uses them? The products stay; only these image links are removed.`)) return;
    setStatus('Removing image(s) from products...');
    try {
      let changed = 0;
      for (const p of products) {
        const has = [p.primary_image, p.secondary_image, ...(p.all_assets || [])].some((u) => selectedImages.has(u));
        if (!has) continue;
        const all = (p.all_assets || []).filter((u) => !selectedImages.has(u));
        const primary = selectedImages.has(p.primary_image) ? (all[0] || '') : p.primary_image;
        const secondary = selectedImages.has(p.secondary_image) ? '' : p.secondary_image;
        const res = await authFetch(SAVE_URL, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(productToBody(p, { primary, secondary, all }, null, activeGido)),
        });
        if (res.ok) changed++;
      }
      setStatus(`Removed from ${changed} product(s)`, 'success');
      setSelectedImages(new Set());
      await load();
    } catch (e) {
      setStatus(`Error: ${e.message}`, 'error');
    }
  }

  // ── archive a product (Glen's "delete the old product after merging its images") ──
  async function archiveProduct(p, e) {
    e.stopPropagation();
    if (!window.confirm(`Archive "${p.name}"? It is removed from the shop but kept (recoverable), not permanently deleted. Move its images to another product first if you want to keep them.`)) return;
    setStatus(`Archiving ${p.name}...`);
    try {
      const res = await authFetch(SAVE_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(productToBody(p, null, 'archived', activeGido)),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) { setStatus(`Archive failed: ${data.reason || res.status}`, 'error'); return; }
      setStatus(`${p.name} archived`, 'success');
      if (selectedProductId === p.product_id) setSelectedProductId(null);
      await load();
    } catch (err) {
      setStatus(`Error: ${err.message}`, 'error');
    }
  }

  return (
    <div style={S.page}>
      <header style={S.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <BackButton onClick={onBack} />
          <h1 style={S.h1}>SmartShop — Asset Library</h1>
        </div>
        <span style={{ fontSize: 10, color: C.faint, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          {activeGido || 'no shop'} · {products.length} products · {library.length} images
        </span>
      </header>

      <div style={S.bar}>
        <span style={{ fontSize: 11, color: selectedProduct ? C.success : C.faint, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          {selectedProduct ? `Selected: ${selectedProduct.name}` : 'No product selected'}
        </span>
        <button style={{ ...S.btn('save'), opacity: (selectedProduct && selectedImages.size) ? 1 : 0.4 }} disabled={!selectedProduct || !selectedImages.size} onClick={assign}>
          Assign {selectedImages.size || ''} to Product
        </button>
        {selectedImages.size > 0 && (
          <>
            <button style={S.btn('ghost')} onClick={() => setSelectedImages(new Set())}>Clear ({selectedImages.size})</button>
            <button style={S.btn('danger')} onClick={unlinkFromAll}>Remove from products</button>
          </>
        )}
      </div>

      <div style={S.cols}>
        {/* PRODUCTS */}
        <div style={S.colProducts}>
          <div style={S.colTitle}><span>Products ({shownProducts.length})</span></div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <input style={S.search} placeholder="Search..." value={pSearch} onChange={(e) => setPSearch(e.target.value)} />
            <select style={S.select} value={pCat} onChange={(e) => setPCat(e.target.value)}>
              <option value="">All</option>
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          {loading && <p style={{ color: C.faint, fontSize: 11 }}>Loading…</p>}
          {shownProducts.map((p) => {
            const imgs = [p.primary_image, ...(p.all_assets || [])].filter((u, i, a) => u && !u.includes('placeholder') && a.indexOf(u) === i);
            const sel = p.product_id === selectedProductId;
            return (
              <div key={p.product_id} style={S.pCard(sel)} onClick={() => {
                setSelectedProductId(sel ? null : p.product_id);
                // smart-sort floats this product's images to the top - scroll there so
                // they are visible without hunting back up the pane.
                if (imagesPaneRef.current) imagesPaneRef.current.scrollTop = 0;
              }}>
                <div style={S.pHead}>
                  <img style={S.pThumb} src={p.primary_image || ''} alt="" onError={(e) => { e.target.style.background = C.bg4; }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={S.pName}>{p.name || 'Unnamed'}</div>
                    <div style={S.pMeta}>{p.category || 'Uncategorised'}</div>
                    <div style={S.pGid}>{p.product_id?.slice(0, 12)}</div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                    <div style={S.pCount}>{imgs.length} imgs</div>
                    <button style={{ ...S.btn('danger'), padding: '3px 7px', fontSize: 8 }} onClick={(e) => archiveProduct(p, e)}>Archive</button>
                  </div>
                </div>
                {imgs.length > 0 && (
                  <div style={S.strip}>
                    {imgs.slice(0, 5).map((u, i) => <img key={i} style={S.stripImg} src={u} alt="" onError={(e) => { e.target.style.display = 'none'; }} />)}
                    {imgs.length > 5 && <span style={{ fontSize: 9, color: C.faint, alignSelf: 'center', paddingLeft: 4 }}>+{imgs.length - 5}</span>}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* IMAGE LIBRARY */}
        <div ref={imagesPaneRef} style={S.colImages}>
          <div style={S.colTitle}>
            <span>Image Library ({shownImages.length})</span>
            <div style={{ display: 'flex', gap: 6 }}>
              {['all', 'used', 'unused'].map((f) => (
                <button key={f} style={S.filterBtn(imgFilter === f)} onClick={() => setImgFilter(f)}>{f}</button>
              ))}
            </div>
          </div>
          {selectedImages.size > 0 && <p style={{ fontSize: 10, color: C.success, marginBottom: 8 }}>{selectedImages.size} selected — pick a product, then Assign</p>}
          <div style={S.imgGrid}>
            {shownImages.map((img) => {
              const usedIn = usageMap[img.url] || [];
              const assigned = assignedToSelected.has(img.url);
              const sel = selectedImages.has(img.url);
              return (
                <div key={img.url} style={S.imgCard(assigned, sel)} onClick={() => toggleImage(img.url)}>
                  {assigned && <div style={S.assignedBadge}>✓ On product</div>}
                  <img style={S.imgTag} src={img.url} alt="" loading="lazy" decoding="async" onError={(e) => { e.target.style.opacity = 0.1; }} />
                  <div style={S.imgInfo}>
                    <div style={S.imgFname} title={img.filename}>{img.filename.slice(0, 22)}{img.filename.length > 22 ? '…' : ''}</div>
                    {usedIn.length > 0
                      ? <div style={S.imgUsed}>Used in {usedIn.length} product{usedIn.length !== 1 ? 's' : ''}</div>
                      : <div style={S.imgUnused}>Unused</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div style={S.statusBar(status.kind)}>{status.msg}</div>
    </div>
  );
}

export default SmartShopAssets;